import { create } from 'zustand';
import { apiClient } from '../lib/api';
import { useAuthStore } from './authStore';

export interface AdminNotification {
  id: string;
  target_role: string;
  target_user_id?: string | null;
  type: string;
  title: string;
  message: string;
  metadata: any;
  is_read: boolean;
  created_at: string;
}

/**
 * The feature a notification belongs to, derived from its server metadata so the
 * UI can group, icon and badge it. Server rows carry `metadata.module` for most
 * events (attendance, shifts, leave, payroll…); access requests and onboarding
 * invites only carry a `type`, so we map those too. Everything else is 'system'.
 */
export function notificationModule(n: AdminNotification): string {
  const m = n.metadata?.module;
  if (typeof m === 'string' && m) return m;
  const t = n.metadata?.type;
  if (t === 'onboarding_invite') return 'onboarding';
  if (t === 'access_request') return 'system';
  return 'system';
}

/** In-app deep link for a notification, from metadata or inferred from its type. */
export function notificationLink(n: AdminNotification): string | undefined {
  const link = n.metadata?.link;
  if (typeof link === 'string' && link) return link;
  if (n.metadata?.type === 'access_request') return '/settings/platform';
  return undefined;
}

interface AdminNotificationState {
  items: AdminNotification[];
  unread: number;
  isLoading: boolean;
  loaded: boolean;
  load: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export const useAdminNotificationStore = create<AdminNotificationState>()((set, get) => ({
  items: [],
  unread: 0,
  isLoading: false,
  loaded: false,

  load: async () => {
    const { isAuthenticated } = useAuthStore.getState();

    // Every signed-in user loads their own server notifications: role-broadcast
    // alerts for admins, and per-user notifications (e.g. "your payslip is
    // ready") for everyone. The server decides what each user is entitled to.
    if (!isAuthenticated) {
      set({ items: [], unread: 0, loaded: true });
      return;
    }
    if (get().isLoading) return;

    set({ isLoading: true });
    try {
      const res = await apiClient.get('/auth/notifications');
      const items: AdminNotification[] = res.data?.data ?? [];
      set({
        items,
        unread: items.filter((i) => !i.is_read).length,
        isLoading: false,
        loaded: true,
      });
    } catch {
      set({ isLoading: false, loaded: true });
    }
  },

  markRead: async (id: string) => {
    try {
      await apiClient.post(`/auth/notifications/${id}/read`);
    } catch {
      /* best-effort */
    }
    const items = get().items.map((i) => (i.id === id ? { ...i, is_read: true } : i));
    set({ items, unread: items.filter((i) => !i.is_read).length });
  },

  markAllRead: async () => {
    const pending = get().items.filter((i) => !i.is_read);
    await Promise.all(
      pending.map((i) => apiClient.post(`/auth/notifications/${i.id}/read`).catch(() => {}))
    );
    const items = get().items.map((i) => ({ ...i, is_read: true }));
    set({ items, unread: 0 });
  },
}));
